import { useState, useEffect, useCallback, useRef, useMemo } from 'react'
import {
  ReactFlow,
  MiniMap,
  Controls,
  Background,
  useNodesState,
  useEdgesState,
  addEdge,
  Connection,
  Edge,
  Node,
  BackgroundVariant,
  Panel,
  MarkerType,
  NodeTypes,
  useReactFlow,
  ReactFlowProvider,
  NodeResizer,
  type Node as FlowNode,
  type Edge as FlowEdge,
  Handle,
  Position,
  getNodesBounds,
  getViewportForBounds,
  SelectionMode,
} from '@xyflow/react'
import '@xyflow/react/dist/style.css'
import { toPng, toSvg } from 'html-to-image'
import jsPDF from 'jspdf'
import {
  Plus,
  Download,
  Upload,
  Save,
  Trash2,
  RefreshCw,
  Cloud,
  Server,
  Database,
  Globe,
  Shield,
  Lock,
  Network,
  HardDrive,
  Layers,
  Box,
  Settings,
  Edit2,
  Eye,
  ChevronDown,
  ChevronUp,
  X,
  Check,
  AlertCircle,
  Sparkles,
  FileImage,
  FileText,
  Type,
  Square,
  Move,
  GripVertical,
  Plug,
  MousePointer2,
} from 'lucide-react'
import api from '../lib/api'

// Custom node component for Azure resources
function AzureResourceNode({ data, selected }: { data: any; selected: boolean }) {
  const getIcon = () => {
    switch (data.resourceType) {
      case 'virtual_machines':
        return <Server className="h-6 w-6" />
      case 'sql_databases':
        return <Database className="h-6 w-6" />
      case 'storage_accounts':
        return <HardDrive className="h-6 w-6" />
      case 'web_apps':
        return <Globe className="h-6 w-6" />
      case 'network_security_groups':
        return <Shield className="h-6 w-6" />
      case 'key_vaults':
        return <Lock className="h-6 w-6" />
      case 'entra_users':
        return <Layers className="h-6 w-6" />
      case 'virtual_networks':
        return <Network className="h-6 w-6" />
      case 'network_interfaces':
        return <Plug className="h-6 w-6" />
      case 'public_ip_addresses':
        return <Globe className="h-6 w-6" />
      case 'virtual_network_gateways':
        return <Network className="h-6 w-6" />
      default:
        return <Box className="h-6 w-6" />
    }
  }

  const getColor = () => {
    switch (data.resourceType) {
      case 'virtual_machines':
        return 'bg-blue-100 border-blue-400 text-blue-700'
      case 'sql_databases':
        return 'bg-purple-100 border-purple-400 text-purple-700'
      case 'storage_accounts':
        return 'bg-green-100 border-green-400 text-green-700'
      case 'web_apps':
        return 'bg-orange-100 border-orange-400 text-orange-700'
      case 'network_security_groups':
        return 'bg-red-100 border-red-400 text-red-700'
      case 'key_vaults':
        return 'bg-yellow-100 border-yellow-400 text-yellow-700'
      case 'entra_users':
        return 'bg-indigo-100 border-indigo-400 text-indigo-700'
      case 'virtual_networks':
        return 'bg-blue-200 border-blue-500 text-blue-800'
      case 'network_interfaces':
        return 'bg-slate-100 border-slate-400 text-slate-700'
      case 'public_ip_addresses':
        return 'bg-emerald-100 border-emerald-400 text-emerald-700'
      case 'virtual_network_gateways':
        return 'bg-violet-100 border-violet-400 text-violet-700'
      default:
        return 'bg-gray-100 border-gray-400 text-gray-700'
    }
  }

  return (
    <div className={`px-4 py-3 rounded-lg border-2 shadow-md min-w-[180px] ${getColor()} ${selected ? 'ring-2 ring-primary-500 ring-offset-2' : ''}`}>
      <Handle type="target" position={Position.Top} className="w-3 h-3 !bg-gray-400" />
      <Handle type="target" position={Position.Left} id="left" className="w-3 h-3 !bg-gray-400" />
      <div className="flex items-center gap-2">
        {getIcon()}
        <div className="flex-1 min-w-0">
          <div className="font-semibold text-sm truncate" title={data.label}>
            {data.label}
          </div>
          {data.subtitle && (
            <div className="text-xs opacity-75 truncate" title={data.subtitle}>
              {data.subtitle}
            </div>
          )}
        </div>
      </div>
      {data.details && (
        <div className="mt-2 text-xs space-y-1 border-t pt-2 opacity-75">
          {data.details.map((detail: string, i: number) => (
            <div key={i} className="truncate" title={detail}>
              {detail}
            </div>
          ))}
        </div>
      )}
      <Handle type="source" position={Position.Bottom} className="w-3 h-3 !bg-gray-400" />
      <Handle type="source" position={Position.Right} id="right" className="w-3 h-3 !bg-gray-400" />
    </div>
  )
}

// Custom node component for AWS resources (orange theme)
function AWSResourceNode({ data, selected }: { data: any; selected: boolean }) {
  const getIcon = () => {
    switch (data.resourceType) {
      case 'ec2_instances':
        return <Server className="h-6 w-6" />
      case 'security_groups':
        return <Shield className="h-6 w-6" />
      case 'rds_instances':
        return <Database className="h-6 w-6" />
      case 'iam_users':
        return <Layers className="h-6 w-6" />
      case 'iam_roles':
        return <Settings className="h-6 w-6" />
      case 'iam_policies':
        return <Lock className="h-6 w-6" />
      case 'vpc':
        return <Network className="h-6 w-6" />
      case 'subnet':
        return <Network className="h-6 w-6" />
      default:
        return <Cloud className="h-6 w-6" />
    }
  }

  const getColor = () => {
    switch (data.resourceType) {
      case 'ec2_instances':
        return 'bg-orange-100 border-orange-400 text-orange-700'
      case 'security_groups':
        return 'bg-red-100 border-red-400 text-red-700'
      case 'rds_instances':
        return 'bg-purple-100 border-purple-400 text-purple-700'
      case 'iam_users':
        return 'bg-yellow-100 border-yellow-400 text-yellow-700'
      case 'iam_roles':
        return 'bg-amber-100 border-amber-400 text-amber-700'
      case 'iam_policies':
        return 'bg-yellow-200 border-yellow-500 text-yellow-800'
      case 'vpc':
        return 'bg-orange-200 border-orange-500 text-orange-800'
      case 'subnet':
        return 'bg-orange-50 border-orange-300 text-orange-600'
      default:
        return 'bg-orange-100 border-orange-400 text-orange-700'
    }
  }

  return (
    <div className={`px-4 py-3 rounded-lg border-2 shadow-md min-w-[180px] ${getColor()} ${selected ? 'ring-2 ring-primary-500 ring-offset-2' : ''}`}>
      <Handle type="target" position={Position.Top} className="w-3 h-3 !bg-gray-400" />
      <Handle type="target" position={Position.Left} id="left" className="w-3 h-3 !bg-gray-400" />
      <div className="flex items-center gap-2">
        {getIcon()}
        <div className="flex-1 min-w-0">
          <div className="font-semibold text-sm truncate" title={data.label}>
            {data.label}
          </div>
          {data.subtitle && (
            <div className="text-xs opacity-75 truncate" title={data.subtitle}>
              {data.subtitle}
            </div>
          )}
        </div>
      </div>
      {data.details && (
        <div className="mt-2 text-xs space-y-1 border-t pt-2 opacity-75">
          {data.details.map((detail: string, i: number) => (
            <div key={i} className="truncate" title={detail}>
              {detail}
            </div>
          ))}
        </div>
      )}
      <Handle type="source" position={Position.Bottom} className="w-3 h-3 !bg-gray-400" />
      <Handle type="source" position={Position.Right} id="right" className="w-3 h-3 !bg-gray-400" />
    </div>
  )
}

// Custom node for user-added components
function CustomNode({ data, selected }: { data: any; selected: boolean }) {
  const getIcon = () => {
    switch (data.customType) {
      case 'waf':
        return <Shield className="h-6 w-6" />
      case 'cdn':
        return <Globe className="h-6 w-6" />
      case 'loadbalancer':
        return <Network className="h-6 w-6" />
      case 'firewall':
        return <Shield className="h-6 w-6" />
      case 'dns':
        return <Globe className="h-6 w-6" />
      default:
        return <Cloud className="h-6 w-6" />
    }
  }

  return (
    <div className={`px-4 py-3 rounded-lg border-2 shadow-md min-w-[180px] ${data.color || 'bg-cyan-100 border-cyan-400 text-cyan-700'} ${selected ? 'ring-2 ring-primary-500 ring-offset-2' : ''}`}>
      <Handle type="target" position={Position.Top} className="w-3 h-3 !bg-gray-400" />
      <Handle type="target" position={Position.Left} id="left" className="w-3 h-3 !bg-gray-400" />
      <div className="flex items-center gap-2">
        {getIcon()}
        <div className="flex-1 min-w-0">
          <div className="font-semibold text-sm truncate" title={data.label}>
            {data.label}
          </div>
          {data.subtitle && (
            <div className="text-xs opacity-75 truncate" title={data.subtitle}>
              {data.subtitle}
            </div>
          )}
        </div>
      </div>
      {data.details && (
        <div className="mt-2 text-xs space-y-1 border-t pt-2 opacity-75">
          {data.details.map((detail: string, i: number) => (
            <div key={i} className="truncate" title={detail}>
              {detail}
            </div>
          ))}
        </div>
      )}
      <Handle type="source" position={Position.Bottom} className="w-3 h-3 !bg-gray-400" />
      <Handle type="source" position={Position.Right} id="right" className="w-3 h-3 !bg-gray-400" />
    </div>
  )
}

// Resource Group container node
function ResourceGroupNode({ data, selected }: { data: any; selected: boolean }) {
  return (
    <div className={`px-4 py-2 rounded-lg border-2 border-dashed border-gray-400 bg-gray-50/50 min-w-[200px] ${selected ? 'ring-2 ring-primary-500 ring-offset-2' : ''}`}>
      <Handle type="target" position={Position.Top} className="w-3 h-3 !bg-gray-400" />
      <Handle type="target" position={Position.Left} id="left" className="w-3 h-3 !bg-gray-400" />
      <div className="flex items-center gap-2 text-gray-600">
        <Layers className="h-5 w-5" />
        <span className="font-medium text-sm">{data.label}</span>
      </div>
      <Handle type="source" position={Position.Bottom} className="w-3 h-3 !bg-gray-400" />
      <Handle type="source" position={Position.Right} id="right" className="w-3 h-3 !bg-gray-400" />
    </div>
  )
}

// Container node for user-defined groupings
function ContainerNode({ data, selected, width, height }: { data: any; selected: boolean; width?: number; height?: number }) {
  const borderStyle = data.borderStyle === 'dashed' ? 'border-dashed' : 'border-solid'

  return (
    <div
      className={`rounded-lg border-2 ${borderStyle} relative`}
      style={{
        borderColor: data.borderColor || '#6b7280',
        backgroundColor: data.backgroundColor || 'rgba(243, 244, 246, 0.5)',
        width: width || data.width || 400,
        height: height || data.height || 300,
        minWidth: 200,
        minHeight: 150,
      }}
    >
      <Handle type="target" position={Position.Top} className="w-3 h-3 !bg-gray-500" />
      <Handle type="target" position={Position.Left} id="left" className="w-3 h-3 !bg-gray-500" />
      <NodeResizer
        minWidth={200}
        minHeight={150}
        isVisible={selected}
        lineClassName="border-primary-500"
        handleClassName="h-3 w-3 bg-white border-2 border-primary-500 rounded"
      />
      <div className="absolute top-0 left-0 right-0 px-3 py-2 bg-white/80 rounded-t-lg border-b border-gray-200">
        <div className="flex items-center gap-2">
          <GripVertical className="h-4 w-4 text-gray-400" />
          <span className="font-medium text-sm text-gray-700">{data.label || 'Container'}</span>
        </div>
      </div>
      <Handle type="source" position={Position.Bottom} className="w-3 h-3 !bg-gray-500" />
      <Handle type="source" position={Position.Right} id="right" className="w-3 h-3 !bg-gray-500" />
    </div>
  )
}

// Text annotation node
function TextAnnotationNode({ data, selected }: { data: any; selected: boolean }) {
  const fontSize = data.fontSize === 'small' ? 'text-sm' : data.fontSize === 'large' ? 'text-xl' : 'text-base'
  const fontWeight = data.bold ? 'font-bold' : 'font-normal'
  const fontStyle = data.italic ? 'italic' : 'not-italic'

  return (
    <div
      className={`${fontSize} ${fontWeight} ${fontStyle} whitespace-pre-wrap max-w-[400px] px-2 py-1 rounded relative ${selected ? 'ring-2 ring-primary-500 ring-offset-2 bg-white' : ''}`}
      style={{ color: data.color || '#374151' }}
    >
      <Handle type="target" position={Position.Top} className="w-2 h-2 !bg-gray-400" />
      <Handle type="target" position={Position.Left} id="left" className="w-2 h-2 !bg-gray-400" />
      {data.text || 'Text annotation'}
      <Handle type="source" position={Position.Bottom} className="w-2 h-2 !bg-gray-400" />
      <Handle type="source" position={Position.Right} id="right" className="w-2 h-2 !bg-gray-400" />
    </div>
  )
}

// VNET container node
function VnetNode({ data, selected, width, height }: { data: any; selected: boolean; width?: number; height?: number }) {
  return (
    <div
      className="rounded-lg border-2 border-solid border-blue-500 relative"
      style={{
        backgroundColor: 'rgba(59, 130, 246, 0.05)',
        width: width || data.width || 600,
        height: height || data.height || 400,
        minWidth: 300,
        minHeight: 200,
      }}
    >
      <Handle type="target" position={Position.Top} className="w-3 h-3 !bg-blue-500" />
      <Handle type="target" position={Position.Left} id="left" className="w-3 h-3 !bg-blue-500" />
      <NodeResizer
        minWidth={300}
        minHeight={200}
        isVisible={selected}
        lineClassName="border-blue-500"
        handleClassName="h-3 w-3 bg-white border-2 border-blue-500 rounded"
      />
      <div className="absolute top-0 left-0 right-0 px-3 py-2 bg-blue-500 text-white rounded-t-lg">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Network className="h-4 w-4" />
            <span className="font-medium text-sm">{data.label || 'Virtual Network'}</span>
          </div>
          {data.addressSpace && (
            <span className="text-xs bg-blue-600 px-2 py-0.5 rounded">{data.addressSpace}</span>
          )}
        </div>
        {data.location && (
          <div className="text-xs opacity-80 mt-1">{data.location}</div>
        )}
      </div>
      <Handle type="source" position={Position.Bottom} className="w-3 h-3 !bg-blue-500" />
      <Handle type="source" position={Position.Right} id="right" className="w-3 h-3 !bg-blue-500" />
    </div>
  )
}

// Subnet container node
function SubnetNode({ data, selected, width, height }: { data: any; selected: boolean; width?: number; height?: number }) {
  return (
    <div
      className="rounded-lg border-2 border-dashed border-indigo-400 relative"
      style={{
        backgroundColor: 'rgba(99, 102, 241, 0.05)',
        width: width || data.width || 350,
        height: height || data.height || 200,
        minWidth: 200,
        minHeight: 100,
      }}
    >
      <Handle type="target" position={Position.Top} className="w-3 h-3 !bg-indigo-400" />
      <Handle type="target" position={Position.Left} id="left" className="w-3 h-3 !bg-indigo-400" />
      <NodeResizer
        minWidth={200}
        minHeight={100}
        isVisible={selected}
        lineClassName="border-indigo-400"
        handleClassName="h-3 w-3 bg-white border-2 border-indigo-400 rounded"
      />
      <div className="absolute top-0 left-0 right-0 px-3 py-2 bg-indigo-100 rounded-t-lg border-b border-indigo-200">
        <div className="flex items-center justify-between">
          <span className="font-medium text-sm text-indigo-700">{data.label || 'Subnet'}</span>
          {data.addressPrefix && (
            <span className="text-xs bg-indigo-200 text-indigo-700 px-2 py-0.5 rounded">{data.addressPrefix}</span>
          )}
        </div>
        {data.nsgName && (
          <div className="text-xs text-indigo-600 mt-1 flex items-center gap-1">
            <Shield className="h-3 w-3" />
            {data.nsgName}
          </div>
        )}
      </div>
      <Handle type="source" position={Position.Bottom} className="w-3 h-3 !bg-indigo-400" />
      <Handle type="source" position={Position.Right} id="right" className="w-3 h-3 !bg-indigo-400" />
    </div>
  )
}

// Public IP node
function PublicIpNode({ data, selected }: { data: any; selected: boolean }) {
  return (
    <div className={`px-4 py-3 rounded-lg border-2 shadow-md min-w-[160px] bg-emerald-100 border-emerald-400 text-emerald-700 ${selected ? 'ring-2 ring-primary-500 ring-offset-2' : ''}`}>
      <Handle type="target" position={Position.Top} className="w-3 h-3 !bg-emerald-400" />
      <Handle type="target" position={Position.Left} id="left" className="w-3 h-3 !bg-emerald-400" />
      <div className="flex items-center gap-2">
        <Globe className="h-5 w-5" />
        <div className="flex-1 min-w-0">
          <div className="font-semibold text-sm truncate" title={data.label}>
            {data.label || 'Public IP'}
          </div>
          {data.ipAddress && (
            <div className="text-xs font-mono bg-emerald-200 px-1.5 py-0.5 rounded mt-1">
              {data.ipAddress}
            </div>
          )}
        </div>
      </div>
      {data.allocationMethod && (
        <div className="mt-2 text-xs opacity-75">
          {data.allocationMethod}
        </div>
      )}
      <Handle type="source" position={Position.Bottom} className="w-3 h-3 !bg-emerald-400" />
      <Handle type="source" position={Position.Right} id="right" className="w-3 h-3 !bg-emerald-400" />
    </div>
  )
}

const nodeTypes: NodeTypes = {
  azureResource: AzureResourceNode,
  awsResource: AWSResourceNode,
  customNode: CustomNode,
  resourceGroup: ResourceGroupNode,
  container: ContainerNode,
  textAnnotation: TextAnnotationNode,
  vnet: VnetNode,
  subnet: SubnetNode,
  publicIp: PublicIpNode,
}

interface Diagram {
  id: string
  name: string
  description?: string
  source_type: string
  source_integration_id?: string
  status: string
  created_at: string
  updated_at: string
}

interface CloudIntegration {
  id: string
  name: string
  type: string
  status: string
}

// Legend configuration
const LEGEND_ITEMS = [
  // Azure resources
  { type: 'virtual_machines', icon: Server, color: 'bg-blue-100 border-blue-400', label: 'Virtual Machines' },
  { type: 'network_security_groups', icon: Shield, color: 'bg-red-100 border-red-400', label: 'Network Security Groups' },
  { type: 'web_apps', icon: Globe, color: 'bg-orange-100 border-orange-400', label: 'Web Apps' },
  { type: 'sql_databases', icon: Database, color: 'bg-purple-100 border-purple-400', label: 'SQL Databases' },
  { type: 'storage_accounts', icon: HardDrive, color: 'bg-green-100 border-green-400', label: 'Storage Accounts' },
  { type: 'key_vaults', icon: Lock, color: 'bg-yellow-100 border-yellow-400', label: 'Key Vaults' },
  { type: 'virtual_networks', icon: Network, color: 'bg-blue-200 border-blue-500', label: 'Virtual Networks' },
  { type: 'network_interfaces', icon: Plug, color: 'bg-slate-100 border-slate-400', label: 'Network Interfaces' },
  { type: 'public_ip_addresses', icon: Globe, color: 'bg-emerald-100 border-emerald-400', label: 'Public IPs' },
  { type: 'virtual_network_gateways', icon: Network, color: 'bg-violet-100 border-violet-400', label: 'VPN Gateways' },
  // AWS resources
  { type: 'ec2_instances', icon: Server, color: 'bg-orange-100 border-orange-400', label: 'EC2 Instances' },
  { type: 'security_groups', icon: Shield, color: 'bg-red-100 border-red-400', label: 'Security Groups (AWS)' },
  { type: 'rds_instances', icon: Database, color: 'bg-purple-100 border-purple-400', label: 'RDS Instances' },
  { type: 'iam_users', icon: Layers, color: 'bg-yellow-100 border-yellow-400', label: 'IAM Users' },
  { type: 'iam_roles', icon: Settings, color: 'bg-amber-100 border-amber-400', label: 'IAM Roles' },
  { type: 'vpc', icon: Network, color: 'bg-orange-200 border-orange-500', label: 'VPC' },
  // Custom resources
  { type: 'waf', icon: Shield, color: 'bg-pink-100 border-pink-400', label: 'WAF' },
  { type: 'cdn', icon: Globe, color: 'bg-teal-100 border-teal-400', label: 'CDN' },
  { type: 'loadbalancer', icon: Network, color: 'bg-amber-100 border-amber-400', label: 'Load Balancer' },
  { type: 'firewall', icon: Shield, color: 'bg-rose-100 border-rose-400', label: 'Firewall' },
  { type: 'dns', icon: Globe, color: 'bg-sky-100 border-sky-400', label: 'DNS' },
  { type: 'vnet', icon: Network, color: 'bg-blue-500 border-blue-500', label: 'Virtual Network' },
  { type: 'subnet', icon: Layers, color: 'bg-indigo-100 border-indigo-400', label: 'Subnet' },
  { type: 'publicIp', icon: Globe, color: 'bg-emerald-100 border-emerald-400', label: 'Public IP' },
  { type: 'other', icon: Cloud, color: 'bg-cyan-100 border-cyan-400', label: 'Custom' },
]

function DiagramsContent() {
  const [diagrams, setDiagrams] = useState<Diagram[]>([])
  const [selectedDiagram, setSelectedDiagram] = useState<Diagram | null>(null)
  const [loading, setLoading] = useState(true)
  const [generating, setGenerating] = useState(false)
  const [saving, setSaving] = useState(false)
  const [cloudIntegrations, setCloudIntegrations] = useState<CloudIntegration[]>([])
  const [selectedIntegration, setSelectedIntegration] = useState<string>('')
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [showAddNodeModal, setShowAddNodeModal] = useState(false)
  const [showAddContainerModal, setShowAddContainerModal] = useState(false)
  const [showAddTextModal, setShowAddTextModal] = useState(false)
  const [showEditModal, setShowEditModal] = useState(false)
  const [editingNode, setEditingNode] = useState<FlowNode | null>(null)
  const [diagramName, setDiagramName] = useState('')
  const [diagramDescription, setDiagramDescription] = useState('')
  const [exportFormat, setExportFormat] = useState<'png' | 'svg' | 'pdf'>('png')
  const [showExportMenu, setShowExportMenu] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [selectionMode, setSelectionMode] = useState(false)

  // Legend state
  const [showLegend, setShowLegend] = useState(true)
  const [legendCollapsed, setLegendCollapsed] = useState(false)

  // Filter state - resource types to hide from diagram
  const [hiddenTypes, setHiddenTypes] = useState<Set<string>>(new Set())

  // Resource group filter - which RGs to show (empty = show all)
  const [selectedResourceGroups, setSelectedResourceGroups] = useState<Set<string>>(new Set())

  // Container modal state
  const [containerLabel, setContainerLabel] = useState('')
  const [containerBorderColor, setContainerBorderColor] = useState('#6b7280')
  const [containerBgColor, setContainerBgColor] = useState('rgba(243, 244, 246, 0.5)')
  const [containerBorderStyle, setContainerBorderStyle] = useState<'solid' | 'dashed'>('solid')

  // Text annotation modal state
  const [textContent, setTextContent] = useState('')
  const [textFontSize, setTextFontSize] = useState<'small' | 'medium' | 'large'>('medium')
  const [textColor, setTextColor] = useState('#374151')
  const [textBold, setTextBold] = useState(false)
  const [textItalic, setTextItalic] = useState(false)

  // Edit modal state
  const [editLabel, setEditLabel] = useState('')
  const [editSubtitle, setEditSubtitle] = useState('')
  const [editDetails, setEditDetails] = useState<string[]>([])

  const reactFlowWrapper = useRef<HTMLDivElement>(null)
  const [nodes, setNodes, onNodesChange] = useNodesState<FlowNode>([])
  const [edges, setEdges, onEdgesChange] = useEdgesState<FlowEdge>([])
  const { fitView, getViewport, screenToFlowPosition } = useReactFlow()

  // Load diagrams and cloud integrations
  useEffect(() => {
    loadDiagrams()
    loadCloudIntegrations()
  }, [])

  const loadDiagrams = async () => {
    try {
      setLoading(true)
      const response = await api.getDiagrams()
      if (response.success) {
        setDiagrams(response.diagrams || [])
      }
    } catch (err) {
      console.error('Failed to load diagrams:', err)
      // If endpoint doesn't exist yet, start with empty list
      setDiagrams([])
    } finally {
      setLoading(false)
    }
  }

  const loadCloudIntegrations = async () => {
    try {
      const response = await api.getIntegrations()
      if (response.success) {
        // Filter to cloud provider integrations
        const cloudTypes = ['azure', 'aws', 'gcp']
        const cloudIntegrations = (response.integrations || []).filter(
          (i: any) => cloudTypes.includes(i.type) && i.status === 'active'
        )
        setCloudIntegrations(cloudIntegrations)
        if (cloudIntegrations.length > 0) {
          setSelectedIntegration(cloudIntegrations[0].id)
        }
      }
    } catch (err) {
      console.error('Failed to load integrations:', err)
    }
  }

  const onConnect = useCallback(
    (params: Connection) => {
      setEdges((eds) =>
        addEdge(
          {
            ...params,
            type: 'smoothstep',
            animated: true,
            markerEnd: { type: MarkerType.ArrowClosed },
          },
          eds
        )
      )
    },
    [setEdges]
  )

  // Double-click node to edit
  const onNodeDoubleClick = useCallback(
    (event: React.MouseEvent, node: FlowNode) => {
      setEditingNode(node)
      const data = node.data as Record<string, any>
      setEditLabel(data.label || '')
      setEditSubtitle(data.subtitle || '')
      setEditDetails(data.details || [])
      setShowEditModal(true)
    },
    []
  )

  // Handle keyboard delete (nodes AND edges)
  const onKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if ((event.key === 'Delete' || event.key === 'Backspace') && !showEditModal && !showAddNodeModal && !showCreateModal) {
        // Delete selected nodes
        const selectedNodes = nodes.filter((n) => n.selected)
        if (selectedNodes.length > 0) {
          const nodeIds = selectedNodes.map((n) => n.id)
          setNodes((nds) => nds.filter((n) => !nodeIds.includes(n.id)))
          setEdges((eds) => eds.filter((e) => !nodeIds.includes(e.source) && !nodeIds.includes(e.target)))
        }

        // Delete selected edges
        const selectedEdges = edges.filter((e) => e.selected)
        if (selectedEdges.length > 0) {
          const edgeIds = selectedEdges.map((e) => e.id)
          setEdges((eds) => eds.filter((e) => !edgeIds.includes(e.id)))
        }
      }
    },
    [nodes, edges, setNodes, setEdges, showEditModal, showAddNodeModal, showCreateModal]
  )

  // Add keyboard listener
  useEffect(() => {
    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [onKeyDown])

  // Save edited node
  const saveEditedNode = useCallback(() => {
    if (!editingNode) return

    setNodes((nds) =>
      nds.map((n) => {
        if (n.id === editingNode.id) {
          return {
            ...n,
            data: {
              ...n.data,
              label: editLabel,
              subtitle: editSubtitle,
              details: editDetails.filter((d) => d.trim() !== ''),
            },
          }
        }
        return n
      })
    )

    setShowEditModal(false)
    setEditingNode(null)
  }, [editingNode, editLabel, editSubtitle, editDetails, setNodes])

  // Delete selected nodes via button
  const deleteSelectedNodes = useCallback(() => {
    const selectedNodes = nodes.filter((n) => n.selected)
    if (selectedNodes.length === 0) return

    if (!confirm(`Delete ${selectedNodes.length} selected node(s)?`)) return

    const nodeIds = selectedNodes.map((n) => n.id)
    setNodes((nds) => nds.filter((n) => !nodeIds.includes(n.id)))
    setEdges((eds) => eds.filter((e) => !nodeIds.includes(e.source) && !nodeIds.includes(e.target)))
  }, [nodes, setNodes, setEdges])

  // Add container node
  const addContainer = useCallback(() => {
    // Calculate viewport center position
    const centerX = window.innerWidth / 2
    const centerY = window.innerHeight / 2
    const flowPosition = screenToFlowPosition({ x: centerX, y: centerY })

    const newNode: Node = {
      id: `container-${Date.now()}`,
      type: 'container',
      position: flowPosition,
      width: 400,
      height: 300,
      data: {
        label: containerLabel || 'Container',
        borderColor: containerBorderColor,
        backgroundColor: containerBgColor,
        borderStyle: containerBorderStyle,
        width: 400,
        height: 300,
      },
      style: { zIndex: -1 },
    }

    setNodes((nds) => [...nds, newNode])
    setContainerLabel('')
    setShowAddContainerModal(false)
  }, [containerLabel, containerBorderColor, containerBgColor, containerBorderStyle, setNodes, screenToFlowPosition])

  // Add text annotation
  const addTextAnnotation = useCallback(() => {
    // Calculate viewport center position
    const centerX = window.innerWidth / 2
    const centerY = window.innerHeight / 2
    const flowPosition = screenToFlowPosition({ x: centerX, y: centerY })

    const newNode: Node = {
      id: `text-${Date.now()}`,
      type: 'textAnnotation',
      position: flowPosition,
      data: {
        text: textContent || 'Text annotation',
        fontSize: textFontSize,
        color: textColor,
        bold: textBold,
        italic: textItalic,
      },
    }

    setNodes((nds) => [...nds, newNode])
    setTextContent('')
    setTextBold(false)
    setTextItalic(false)
    setShowAddTextModal(false)
  }, [textContent, textFontSize, textColor, textBold, textItalic, setNodes, screenToFlowPosition])

  // Helper to extract resource type from a node
  const getNodeResourceType = useCallback((node: FlowNode): string | null => {
    const data = node.data as Record<string, any>
    if (node.type === 'azureResource' && data.resourceType) {
      return data.resourceType
    }
    if (node.type === 'customNode' && data.customType) {
      return data.customType
    }
    if (node.type === 'vnet') return 'vnet'
    if (node.type === 'subnet') return 'subnet'
    if (node.type === 'publicIp') return 'publicIp'
    if (node.type === 'resourceGroup') return 'resourceGroup'
    return null
  }, [])

  // Helper to get resource group from a node
  const getNodeResourceGroup = useCallback((node: FlowNode): string | null => {
    const data = node.data as Record<string, any>
    // Container nodes (resource groups) have id like 'rg-{name}'
    if (node.type === 'container' && node.id.startsWith('rg-')) {
      return data.label || node.id.replace('rg-', '')
    }
    // Regular resource nodes have RG in subtitle
    if (data.subtitle) {
      return data.subtitle
    }
    return null
  }, [])

  // Get unique resource groups present in diagram
  const availableResourceGroups = useMemo(() => {
    const rgs = new Set<string>()
    nodes.forEach((node) => {
      const rg = getNodeResourceGroup(node)
      if (rg) rgs.add(rg)
    })
    return Array.from(rgs).sort()
  }, [nodes, getNodeResourceGroup])

  // Get legend items present in current diagram
  const activeLegendItems = useMemo(() => {
    const presentTypes = new Set<string>()
    nodes.forEach((node) => {
      const resourceType = getNodeResourceType(node)
      if (resourceType) presentTypes.add(resourceType)
    })
    return LEGEND_ITEMS.filter((item) => presentTypes.has(item.type))
  }, [nodes, getNodeResourceType])

  // Filter nodes based on hiddenTypes and selectedResourceGroups
  const filteredNodes = useMemo(() => {
    const hasTypeFilter = hiddenTypes.size > 0
    const hasRGFilter = selectedResourceGroups.size > 0

    if (!hasTypeFilter && !hasRGFilter) return nodes

    return nodes.filter((node) => {
      // Check resource type filter
      const resourceType = getNodeResourceType(node)
      if (resourceType && hiddenTypes.has(resourceType)) {
        return false
      }

      // Check resource group filter (if any RGs selected, only show those)
      if (hasRGFilter) {
        const nodeRG = getNodeResourceGroup(node)
        // Keep nodes without an RG (text annotations, manual containers, etc.)
        if (!nodeRG) return true
        return selectedResourceGroups.has(nodeRG)
      }

      return true
    })
  }, [nodes, hiddenTypes, selectedResourceGroups, getNodeResourceType, getNodeResourceGroup])

  // Track which nodes are visible for edge filtering
  const visibleNodeIds = useMemo(() => {
    return new Set(filteredNodes.map((n) => n.id))
  }, [filteredNodes])

  // Filter edges - hide edges where either source or target is hidden
  const filteredEdges = useMemo(() => {
    const hasFilters = hiddenTypes.size > 0 || selectedResourceGroups.size > 0
    if (!hasFilters) return edges
    return edges.filter((edge) => {
      return visibleNodeIds.has(edge.source) && visibleNodeIds.has(edge.target)
    })
  }, [edges, hiddenTypes, selectedResourceGroups, visibleNodeIds])

  // Toggle a resource type visibility
  const toggleTypeVisibility = useCallback((type: string) => {
    setHiddenTypes((prev) => {
      const next = new Set(prev)
      if (next.has(type)) {
        next.delete(type)
      } else {
        next.add(type)
      }
      return next
    })
  }, [])

  // Toggle a resource group visibility
  const toggleResourceGroup = useCallback((rg: string) => {
    setSelectedResourceGroups((prev) => {
      const next = new Set(prev)
      if (next.has(rg)) {
        next.delete(rg)
      } else {
        next.add(rg)
      }
      return next
    })
  }, [])

  // Clear all filters
  const clearAllFilters = useCallback(() => {
    setHiddenTypes(new Set())
    setSelectedResourceGroups(new Set())
  }, [])

  // Generate diagram from cloud integration data
  const generateFromCloud = async () => {
    if (!selectedIntegration) {
      setError('Please select a cloud integration')
      return
    }

    setGenerating(true)
    setError(null)

    try {
      // Get the integration type for filtering evidence by source
      const integration = cloudIntegrations.find(i => i.id === selectedIntegration)
      // Evidence source is stored as "{type}_integration" (e.g., "azure_integration")
      const sourceType = integration?.type ? `${integration.type}_integration` : 'azure_integration'

      // Fetch evidence items from the integration (filter by source/type)
      const evidenceResponse = await api.getEvidence({
        source: sourceType,
        limit: 100,
      })

      if (!evidenceResponse.success || !evidenceResponse.evidence?.length) {
        setError('No cloud resources found for this integration')
        setGenerating(false)
        return
      }

      const generatedNodes: Node[] = []
      const generatedEdges: Edge[] = []

      // ============================================================
      // AWS DIAGRAM GENERATION
      // ============================================================
      if (integration?.type === 'aws') {
        console.log('[AWS DIAGRAM] Starting AWS diagram generation')

        // AWS topology structures
        const vpcs = new Map<string, { name: string; instances: any[]; securityGroups: any[] }>()
        const ec2ToSG = new Map<string, string[]>() // Instance ID -> Security Group IDs
        const sgNodes = new Map<string, string>() // Security Group ID -> node ID
        const ec2Nodes = new Map<string, string>() // Instance ID -> node ID
        const rdsNodes = new Map<string, string>() // RDS ID -> node ID

        // First pass: collect all AWS data
        evidenceResponse.evidence.forEach((evidence: any) => {
          const metadata = typeof evidence.metadata === 'string'
            ? JSON.parse(evidence.metadata)
            : evidence.metadata

          if (!metadata || !metadata.data) return

          const resourceType = metadata.type || 'unknown'
          const resourceData = Array.isArray(metadata.data) ? metadata.data : []

          console.log(`[AWS DIAGRAM] Processing ${resourceType}:`, resourceData.length, 'items')

          // Process EC2 Instances
          if (resourceType === 'ec2_instances') {
            resourceData.forEach((instance: any) => {
              const vpcId = instance.VpcId || 'no-vpc'
              if (!vpcs.has(vpcId)) {
                vpcs.set(vpcId, { name: vpcId, instances: [], securityGroups: [] })
              }
              vpcs.get(vpcId)!.instances.push(instance)

              // Track EC2 → Security Group relationships
              // Security groups might be in SecurityGroups field or extracted from XML
              if (instance.SecurityGroups) {
                ec2ToSG.set(instance.InstanceId, instance.SecurityGroups)
              }
            })
          }

          // Process Security Groups
          if (resourceType === 'security_groups') {
            resourceData.forEach((sg: any) => {
              const vpcId = sg.VpcId || 'no-vpc'
              if (!vpcs.has(vpcId)) {
                vpcs.set(vpcId, { name: vpcId, instances: [], securityGroups: [] })
              }
              vpcs.get(vpcId)!.securityGroups.push(sg)
            })
          }
        })

        // Second pass: create nodes
        let currentX = 50
        let currentY = 50
        const nodeW = 180
        const nodeH = 100
        const nodePad = 20
        const vpcPad = 30
        const vpcHeader = 40

        // Create VPC containers with their resources
        vpcs.forEach((vpcData, vpcId) => {
          const resources: Node[] = []

          // Create EC2 instance nodes
          vpcData.instances.forEach((instance, i) => {
            const nodeId = `ec2-${instance.InstanceId || i}`
            const node: Node = {
              id: nodeId,
              type: 'awsResource',
              position: { x: 0, y: 0 },
              data: {
                label: instance.Name || instance.InstanceId || `Instance ${i + 1}`,
                resourceType: 'ec2_instances',
                subtitle: instance.InstanceType || '',
                details: [
                  instance.State ? `State: ${instance.State}` : '',
                  instance.PrivateIpAddress ? `Private: ${instance.PrivateIpAddress}` : '',
                  instance.PublicIpAddress ? `Public: ${instance.PublicIpAddress}` : '',
                  instance.Platform || 'Linux',
                ].filter(Boolean),
                resourceId: instance.InstanceId,
              },
            }
            resources.push(node)
            ec2Nodes.set(instance.InstanceId, nodeId)
          })

          // Create Security Group nodes
          vpcData.securityGroups.forEach((sg, i) => {
            const nodeId = `sg-${sg.GroupId || i}`
            const inboundCount = sg.InboundRules?.length || 0
            const outboundCount = sg.OutboundRules?.length || 0
            const node: Node = {
              id: nodeId,
              type: 'awsResource',
              position: { x: 0, y: 0 },
              data: {
                label: sg.GroupName || sg.GroupId || `Security Group ${i + 1}`,
                resourceType: 'security_groups',
                subtitle: sg.GroupId || '',
                details: [
                  `Inbound: ${inboundCount} rules`,
                  `Outbound: ${outboundCount} rules`,
                  sg.Description || '',
                ].filter(Boolean),
                resourceId: sg.GroupId,
              },
            }
            resources.push(node)
            sgNodes.set(sg.GroupId, nodeId)
          })

          // Layout resources in VPC
          const cols = resources.length <= 4 ? 2 : resources.length <= 9 ? 3 : 4
          const rows = Math.ceil(resources.length / cols)
          const vpcW = cols * (nodeW + nodePad) + vpcPad * 2
          const vpcH = vpcHeader + rows * (nodeH + nodePad) + vpcPad

          resources.forEach((node, i) => {
            const col = i % cols
            const row = Math.floor(i / cols)
            node.position = {
              x: currentX + vpcPad + col * (nodeW + nodePad),
              y: currentY + vpcHeader + row * (nodeH + nodePad),
            }
            generatedNodes.push(node)
          })

          // Create VPC container
          if (vpcId !== 'no-vpc') {
            generatedNodes.push({
              id: `vpc-${vpcId}`,
              type: 'container',
              position: { x: currentX, y: currentY },
              width: vpcW,
              height: vpcH,
              data: {
                label: `VPC: ${vpcId}`,
                width: vpcW,
                height: vpcH,
                borderColor: '#f97316', // orange
                backgroundColor: 'rgba(249, 115, 22, 0.05)',
                borderStyle: 'solid',
              },
              style: { zIndex: -1 },
            })
          }

          currentX += vpcW + 40
          if (currentX > 1500) {
            currentX = 50
            currentY += vpcH + 40
          }
        })

        // Process RDS, IAM outside VPCs
        let otherY = currentY + 200
        let otherX = 50

        evidenceResponse.evidence.forEach((evidence: any) => {
          const metadata = typeof evidence.metadata === 'string'
            ? JSON.parse(evidence.metadata)
            : evidence.metadata

          if (!metadata || !metadata.data) return

          const resourceType = metadata.type || 'unknown'
          const resourceData = Array.isArray(metadata.data) ? metadata.data : []

          // RDS Instances
          if (resourceType === 'rds_instances') {
            resourceData.forEach((rds: any, i: number) => {
              const nodeId = `rds-${rds.DBInstanceIdentifier || i}`
              generatedNodes.push({
                id: nodeId,
                type: 'awsResource',
                position: { x: otherX, y: otherY },
                data: {
                  label: rds.DBInstanceIdentifier || `RDS ${i + 1}`,
                  resourceType: 'rds_instances',
                  subtitle: rds.Engine || '',
                  details: [
                    rds.DBInstanceClass || '',
                    rds.DBInstanceStatus ? `Status: ${rds.DBInstanceStatus}` : '',
                    rds.MultiAZ ? 'Multi-AZ: Yes' : 'Multi-AZ: No',
                    rds.StorageEncrypted ? 'Encrypted: Yes' : 'Encrypted: No',
                  ].filter(Boolean),
                  resourceId: rds.DBInstanceIdentifier,
                },
              })
              rdsNodes.set(rds.DBInstanceIdentifier, nodeId)
              otherX += nodeW + nodePad
            })
          }

          // IAM Users
          if (resourceType === 'iam_users') {
            resourceData.forEach((user: any, i: number) => {
              generatedNodes.push({
                id: `iam-user-${user.UserName || i}`,
                type: 'awsResource',
                position: { x: otherX, y: otherY },
                data: {
                  label: user.UserName || `User ${i + 1}`,
                  resourceType: 'iam_users',
                  subtitle: 'IAM User',
                  details: [
                    user.Arn || '',
                    user.CreateDate ? `Created: ${user.CreateDate.split('T')[0]}` : '',
                  ].filter(Boolean),
                },
              })
              otherX += nodeW + nodePad
            })
          }

          // IAM Roles
          if (resourceType === 'iam_roles') {
            resourceData.forEach((role: any, i: number) => {
              generatedNodes.push({
                id: `iam-role-${role.RoleName || i}`,
                type: 'awsResource',
                position: { x: otherX, y: otherY },
                data: {
                  label: role.RoleName || `Role ${i + 1}`,
                  resourceType: 'iam_roles',
                  subtitle: 'IAM Role',
                  details: [
                    role.Description || '',
                  ].filter(Boolean),
                },
              })
              otherX += nodeW + nodePad
            })
          }

          if (otherX > 1500) {
            otherX = 50
            otherY += nodeH + nodePad
          }
        })

        // Create edges: EC2 → Security Groups
        ec2ToSG.forEach((sgIds, instanceId) => {
          const ec2NodeId = ec2Nodes.get(instanceId)
          if (ec2NodeId && sgIds) {
            sgIds.forEach((sgId: string) => {
              const sgNodeId = sgNodes.get(sgId)
              if (sgNodeId) {
                generatedEdges.push({
                  id: `edge-ec2-sg-${instanceId}-${sgId}`,
                  source: ec2NodeId,
                  target: sgNodeId,
                  type: 'smoothstep',
                  animated: true,
                  markerEnd: { type: MarkerType.ArrowClosed },
                })
              }
            })
          }
        })

        console.log('[AWS DIAGRAM] Generated', generatedNodes.length, 'nodes and', generatedEdges.length, 'edges')

        setNodes(generatedNodes)
        setEdges(generatedEdges)
        setGenerating(false)
        return // Exit early for AWS
      }

      // ============================================================
      // AZURE DIAGRAM GENERATION (existing code)
      // ============================================================

      // Network topology structures
      const vnets = new Map<string, { name: string; addressSpace: string[]; subnets: Map<string, { addressPrefix: string; nsgName: string; vms: string[] }>; y: number }>()
      const vmToNic = new Map<string, string[]>() // VM Name -> NIC names
      const nicToSubnet = new Map<string, { vnet: string; subnet: string; privateIp: string }>() // NIC name -> VNET/Subnet/IP
      const nicToNsg = new Map<string, string>() // NIC name -> NSG name
      const nsgToSubnet = new Map<string, { vnet: string; subnet: string }>() // NSG name -> VNET/Subnet
      const nsgNodes = new Map<string, string>() // NSG name -> node ID
      const nicNodes = new Map<string, string>() // NIC name (lowercase) -> node ID
      const pipNodes = new Map<string, string>() // Public IP name -> node ID
      const nicToPip = new Map<string, string>() // NIC name (lowercase) -> Public IP name
      const publicIps = new Map<string, { name: string; ipAddress: string; associatedTo: string; resourceGroup: string }>() // Public IP data
      const vnetGateways: any[] = [] // Virtual Network Gateways
      const resourceGroups = new Map<string, Node[]>()
      const vmsInVnets = new Set<string>() // Track VMs that are placed in VNETs

      // First pass: collect all network data
      evidenceResponse.evidence.forEach((evidence: any) => {
        const metadata = typeof evidence.metadata === 'string'
          ? JSON.parse(evidence.metadata)
          : evidence.metadata

        if (!metadata || !metadata.data) return

        const resourceType = metadata.type || 'unknown'
        const resourceData = Array.isArray(metadata.data) ? metadata.data : []

        // Process Virtual Networks (new)
        if (resourceType === 'virtual_networks') {
          resourceData.forEach((vnet: any) => {
            if (!vnets.has(vnet.Name)) {
              vnets.set(vnet.Name, {
                name: vnet.Name,
                addressSpace: vnet.AddressSpace || [],
                subnets: new Map(),
                y: 0
              })
            }
            const vnetEntry = vnets.get(vnet.Name)!
            // Add subnets from VNET data
            if (vnet.Subnets && Array.isArray(vnet.Subnets)) {
              vnet.Subnets.forEach((subnet: any) => {
                if (!vnetEntry.subnets.has(subnet.Name)) {
                  // Extract NSG name from path
                  let nsgName = ''
                  if (subnet.NetworkSecurityGroup) {
                    nsgName = subnet.NetworkSecurityGroup.split('/').pop() || ''
                  }
                  vnetEntry.subnets.set(subnet.Name, {
                    addressPrefix: subnet.AddressPrefix || '',
                    nsgName: nsgName,
                    vms: []
                  })
                }
              })
            }
          })
        }

        // Process Network Interfaces (new) - direct NIC→Subnet mapping
        if (resourceType === 'network_interfaces') {
          resourceData.forEach((nic: any) => {
            const nicName = nic.Name?.toLowerCase() || ''
            if (nicName && nic.VirtualNetwork && nic.Subnet) {
              nicToSubnet.set(nicName, {
                vnet: nic.VirtualNetwork,
                subnet: nic.Subnet,
                privateIp: nic.PrimaryPrivateIP || ''
              })
            }
            // Also track NSG from NIC
            if (nicName && nic.NetworkSecurityGroup) {
              const nsgName = nic.NetworkSecurityGroup.split('/').pop() || ''
              if (nsgName) {
                nicToNsg.set(nicName, nsgName)
              }
            }
            // Track NIC→Public IP relationship
            if (nicName && nic.IpConfigurations) {
              nic.IpConfigurations.forEach((ipConfig: any) => {
                if (ipConfig.PublicIPAddress) {
                  const pipName = ipConfig.PublicIPAddress.split('/').pop() || ''
                  if (pipName) {
                    nicToPip.set(nicName, pipName)
                  }
                }
              })
            }
          })
        }

        // Process Public IP Addresses (new)
        if (resourceType === 'public_ip_addresses') {
          resourceData.forEach((pip: any) => {
            if (pip.IpAddress) {
              publicIps.set(pip.Name, {
                name: pip.Name,
                ipAddress: pip.IpAddress,
                associatedTo: pip.IpConfiguration || '',
                resourceGroup: pip.ResourceGroup || 'Default'
              })
            }
          })
        }

        // Process Virtual Network Gateways (new)
        if (resourceType === 'virtual_network_gateways') {
          resourceData.forEach((gw: any) => {
            vnetGateways.push(gw)
          })
        }

        // Process NSGs (existing logic, but also extract subnet mapping)
        if (resourceType === 'network_security_groups') {
          resourceData.forEach((nsg: any) => {
            // Extract VNET/Subnet from Subnets array (NSGs with subnet-level attachment)
            if (nsg.Subnets && Array.isArray(nsg.Subnets) && nsg.Subnets.length > 0) {
              nsg.Subnets.forEach((subnetPath: string) => {
                const vnetMatch = subnetPath.match(/virtualNetworks\/([^/]+)\/subnets\/([^/]+)/)
                if (vnetMatch) {
                  const [, vnetName, subnetName] = vnetMatch
                  nsgToSubnet.set(nsg.Name, { vnet: vnetName, subnet: subnetName })
                  // Ensure VNET/Subnet exists in our map
                  if (!vnets.has(vnetName)) {
                    vnets.set(vnetName, { name: vnetName, addressSpace: [], subnets: new Map(), y: 0 })
                  }
                  const vnet = vnets.get(vnetName)!
                  if (!vnet.subnets.has(subnetName)) {
                    vnet.subnets.set(subnetName, { addressPrefix: '', nsgName: nsg.Name, vms: [] })
                  } else {
                    // Update NSG name if not set
                    const subnet = vnet.subnets.get(subnetName)!
                    if (!subnet.nsgName) subnet.nsgName = nsg.Name
                  }
                }
              })
            }

            // Map NICs to their NSG (fallback if NIC data not available)
            if (nsg.NetworkInterfaces && Array.isArray(nsg.NetworkInterfaces)) {
              nsg.NetworkInterfaces.forEach((nicPath: string) => {
                const nicName = (nicPath.split('/').pop() || '').toLowerCase()
                if (nicName && !nicToNsg.has(nicName)) {
                  nicToNsg.set(nicName, nsg.Name)
                }
              })
            }
          })
        }

        // Process VMs
        if (resourceType === 'virtual_machines') {
          resourceData.forEach((vm: any) => {
            if (vm.NetworkInterfaces && Array.isArray(vm.NetworkInterfaces)) {
              const nicNames = vm.NetworkInterfaces.map((nic: string) => {
                return (nic.split('/').pop() || '').toLowerCase()
              })
              vmToNic.set(vm.Name, nicNames)
            }
          })
        }
      })

      // DEBUG: Log collected data
      console.log('[DIAGRAM DEBUG] vmToNic:', Object.fromEntries(vmToNic))
      console.log('[DIAGRAM DEBUG] nicToSubnet:', Object.fromEntries(nicToSubnet))
      console.log('[DIAGRAM DEBUG] vnets:', Object.fromEntries(Array.from(vnets.entries()).map(([k, v]) => [k, { ...v, subnets: Object.fromEntries(v.subnets) }])))

      // Determine VM→VNET/Subnet placement using NIC data (preferred) or NSG chain (fallback)
      vmToNic.forEach((nicNames, vmName) => {
        console.log(`[DIAGRAM DEBUG] Processing VM "${vmName}" with NICs:`, nicNames)
        for (const nicName of nicNames) {
          // First try direct NIC→Subnet mapping (from network_interfaces evidence)
          const nicSubnet = nicToSubnet.get(nicName)
          console.log(`[DIAGRAM DEBUG]   NIC "${nicName}" -> nicSubnet:`, nicSubnet)
          if (nicSubnet) {
            const vnet = vnets.get(nicSubnet.vnet)
            if (vnet) {
              let subnet = vnet.subnets.get(nicSubnet.subnet)
              if (!subnet) {
                // Create subnet if it doesn't exist
                vnet.subnets.set(nicSubnet.subnet, { addressPrefix: '', nsgName: '', vms: [] })
                subnet = vnet.subnets.get(nicSubnet.subnet)!
              }
              subnet.vms.push(vmName)
              vmsInVnets.add(vmName)
              break
            }
          }

          // Fallback: NIC→NSG→Subnet chain
          const nsgName = nicToNsg.get(nicName)
          if (nsgName) {
            const subnetInfo = nsgToSubnet.get(nsgName)
            if (subnetInfo) {
              const vnet = vnets.get(subnetInfo.vnet)
              if (vnet) {
                const subnet = vnet.subnets.get(subnetInfo.subnet)
                if (subnet) {
                  subnet.vms.push(vmName)
                  vmsInVnets.add(vmName)
                  break
                }
              }
            }
          }
        }
      })

      // Second pass: process all resources and create nodes
      evidenceResponse.evidence.forEach((evidence: any) => {
        const metadata = typeof evidence.metadata === 'string'
          ? JSON.parse(evidence.metadata)
          : evidence.metadata

        if (!metadata || !metadata.data) return

        const resourceType = metadata.type || 'unknown'

        // Skip user types for diagram - they don't represent infrastructure
        if (resourceType === 'entra_users') return

        // Skip VNETs - they're rendered as container nodes from the vnets Map
        if (resourceType === 'virtual_networks') return

        const resourceData = Array.isArray(metadata.data) ? metadata.data : []

        resourceData.forEach((resource: any, resourceIndex: number) => {
          const resourceGroupName = resource.ResourceGroup || resource.resource_group || 'Default'

          const node: Node = {
            id: `${resourceType}-${resourceIndex}-${resource.Id || resource.Name || resourceIndex}`,
            type: 'azureResource',
            position: { x: 0, y: 0 },
            data: {
              label: resource.Name || resource.DisplayName || `Resource ${resourceIndex + 1}`,
              resourceType: resourceType,
              subtitle: resourceGroupName,
              details: getResourceDetails(resourceType, resource),
              resourceId: resource.Id,
              resourceName: resource.Name,
            },
          }

          // Track NSG nodes for edge creation
          if (resourceType === 'network_security_groups') {
            nsgNodes.set(resource.Name, node.id)
          }

          // Track NIC nodes for edge creation (by lowercase name to match vmToNic)
          if (resourceType === 'network_interfaces') {
            nicNodes.set(resource.Name?.toLowerCase(), node.id)
          }

          // Track Public IP nodes for edge creation
          if (resourceType === 'public_ip_addresses') {
            pipNodes.set(resource.Name, node.id)
          }

          if (!resourceGroups.has(resourceGroupName)) {
            resourceGroups.set(resourceGroupName, [])
          }
          resourceGroups.get(resourceGroupName)!.push(node)
        })
      })

      // ============================================================
      // RESOURCE GROUP-FIRST LAYOUT
      // Everything organized by Azure Resource Group
      // ============================================================

      // Add VNETs to their resource groups
      evidenceResponse.evidence.forEach((ev: any) => {
        const meta = typeof ev.metadata === 'string' ? JSON.parse(ev.metadata) : ev.metadata
        if (meta?.type === 'virtual_networks' && meta?.data) {
          meta.data.forEach((vnet: any) => {
            const rgName = vnet.ResourceGroup || 'Default'
            const vnetData = vnets.get(vnet.Name)
            const vnetNode: Node = {
              id: `vnet-${vnet.Name}`,
              type: 'azureResource',
              position: { x: 0, y: 0 },
              data: {
                label: vnet.Name,
                resourceType: 'virtual_networks',
                subtitle: rgName,
                details: [vnetData?.addressSpace?.join(', ') || vnet.AddressSpace?.AddressPrefixes?.join(', ') || ''],
                resourceName: vnet.Name,
              },
            }
            if (!resourceGroups.has(rgName)) resourceGroups.set(rgName, [])
            resourceGroups.get(rgName)!.push(vnetNode)
          })
        }
      })

      // Add Public IPs to their resource groups
      publicIps.forEach((pipData, pipName) => {
        const rgName = pipData.resourceGroup || 'Default'
        const pipNode: Node = {
          id: `pip-${pipName}`,
          type: 'publicIp',
          position: { x: 0, y: 0 },
          data: {
            label: pipName,
            ipAddress: pipData.ipAddress,
            resourceName: pipName,
            resourceType: 'public_ip_addresses',
          },
        }
        if (!resourceGroups.has(rgName)) resourceGroups.set(rgName, [])
        resourceGroups.get(rgName)!.push(pipNode)
        pipNodes.set(pipName, pipNode.id)
      })

      // Add VPN Gateways to their resource groups
      vnetGateways.forEach((gw, i) => {
        const rgName = gw.ResourceGroup || 'Default'
        const gwNode: Node = {
          id: `gw-${gw.Name || i}`,
          type: 'azureResource',
          position: { x: 0, y: 0 },
          data: {
            label: gw.Name || `Gateway ${i + 1}`,
            resourceType: 'virtual_network_gateways',
            subtitle: rgName,
            details: [gw.GatewayType, gw.SKU].filter(Boolean),
            resourceName: gw.Name,
          },
        }
        if (!resourceGroups.has(rgName)) resourceGroups.set(rgName, [])
        resourceGroups.get(rgName)!.push(gwNode)
      })

      // Layout: All resource groups in a grid
      let currentX = 50
      let currentY = 50
      let rowMaxHeight = 0
      const nodeW = 180
      const nodeH = 100
      const nodePad = 15
      const rgPad = 20
      const rgHeader = 35
      const maxCanvasWidth = 2000

      // Sort RGs by name for consistent layout
      const sortedRGs = Array.from(resourceGroups.entries())
        .filter(([_, nodes]) => nodes.length > 0)
        .sort((a, b) => a[0].localeCompare(b[0]))

      sortedRGs.forEach(([rgName, nodes]) => {
        // More columns for larger RGs to reduce height
        const cols = nodes.length <= 4 ? 2 : nodes.length <= 9 ? 3 : nodes.length <= 16 ? 4 : 5
        const rows = Math.ceil(nodes.length / cols)
        const rgW = cols * (nodeW + nodePad) + rgPad * 2
        const rgH = rgHeader + rows * (nodeH + nodePad) + rgPad

        console.log(`[RG] ${rgName}: ${nodes.length} nodes, ${cols} cols, ${rows} rows, size ${rgW}x${rgH}`)

        // Wrap to next row if needed
        if (currentX + rgW > maxCanvasWidth && currentX > 50) {
          currentX = 50
          currentY += rowMaxHeight + 30
          rowMaxHeight = 0
        }

        // Position nodes inside FIRST (so we can verify positions)
        const nodePositions: { x: number; y: number }[] = []
        nodes.forEach((node, i) => {
          const col = i % cols
          const row = Math.floor(i / cols)
          const nodeX = currentX + rgPad + col * (nodeW + nodePad)
          const nodeY = currentY + rgHeader + row * (nodeH + nodePad)
          node.position = { x: nodeX, y: nodeY }
          nodePositions.push({ x: nodeX, y: nodeY })
          generatedNodes.push(node)
        })

        // Calculate actual bounding box from node positions
        const maxNodeY = Math.max(...nodePositions.map(p => p.y)) + nodeH
        const actualHeight = maxNodeY - currentY + rgPad

        // Create RG container with actual calculated height
        generatedNodes.push({
          id: `rg-${rgName}`,
          type: 'container',
          position: { x: currentX, y: currentY },
          data: {
            label: rgName,
            width: rgW,
            height: actualHeight,
            borderColor: '#6b7280',
            backgroundColor: 'rgba(107, 114, 128, 0.05)',
            borderStyle: 'solid',
          },
          style: { zIndex: -1 },
        })

        currentX += rgW + 30
        rowMaxHeight = Math.max(rowMaxHeight, actualHeight)
      })









      // DEBUG: Log all node IDs and maps for edge creation
      console.warn('=== EDGE CREATION CODE REACHED ===')
      console.warn(`[EDGE] generatedNodes count: ${generatedNodes.length}`)
      console.warn(`[EDGE] vmToNic size: ${vmToNic.size}`)
      console.warn(`[EDGE] nicToPip size: ${nicToPip.size}`)
      console.warn(`[EDGE] nsgToSubnet size: ${nsgToSubnet.size}`)

      // Create edges by iterating through relationship maps directly
      // VM -> NIC edges
      vmToNic.forEach((nicNames, vmName) => {
        // Find VM node
        const vmNode = generatedNodes.find(n => {
          const d = n.data as any
          return d.resourceType === 'virtual_machines' &&
                 (d.resourceName === vmName || d.resourceName?.toLowerCase() === vmName.toLowerCase())
        })
        if (!vmNode) {
          console.log(`[EDGE DEBUG] VM not found: ${vmName}`)
          return
        }

        nicNames.forEach(nicName => {
          // Find NIC node - try both lowercase and original
          const nicNode = generatedNodes.find(n => {
            const d = n.data as any
            return d.resourceType === 'network_interfaces' &&
                   (d.resourceName?.toLowerCase() === nicName.toLowerCase() || d.resourceName === nicName)
          })
          if (nicNode) {
            console.log(`[EDGE DEBUG] Creating VM->NIC edge: ${vmNode.id} -> ${nicNode.id}`)
            generatedEdges.push({
              id: `edge-vm-nic-${vmNode.id}-${nicNode.id}`,
              source: vmNode.id,
              target: nicNode.id,
              type: 'smoothstep',
              markerEnd: { type: MarkerType.ArrowClosed },
              style: { stroke: '#6366f1', strokeWidth: 2 },
              label: 'uses',
            })
          } else {
            console.log(`[EDGE DEBUG] NIC not found: ${nicName}`)
          }
        })
      })

      // NIC -> Public IP edges
      nicToPip.forEach((pipName, nicName) => {
        const nicNode = generatedNodes.find(n => {
          const d = n.data as any
          return d.resourceType === 'network_interfaces' &&
                 (d.resourceName?.toLowerCase() === nicName.toLowerCase() || d.resourceName === nicName)
        })
        const pipNode = generatedNodes.find(n => {
          const d = n.data as any
          return (d.resourceType === 'public_ip_addresses' || n.type === 'publicIp') &&
                 (d.resourceName === pipName || d.label === pipName)
        })
        if (nicNode && pipNode) {
          console.log(`[EDGE DEBUG] Creating NIC->PIP edge: ${nicNode.id} -> ${pipNode.id}`)
          generatedEdges.push({
            id: `edge-nic-pip-${nicNode.id}-${pipNode.id}`,
            source: nicNode.id,
            target: pipNode.id,
            type: 'smoothstep',
            markerEnd: { type: MarkerType.ArrowClosed },
            style: { stroke: '#10b981', strokeWidth: 2 },
            label: 'has IP',
          })
        }
      })

      // NSG -> Subnet edges (NSGs protect subnets)
      nsgToSubnet.forEach((subnetInfo, nsgName) => {
        const nsgNode = generatedNodes.find(n => {
          const d = n.data as any
          return d.resourceType === 'network_security_groups' && d.resourceName === nsgName
        })
        const subnetNode = generatedNodes.find(n =>
          n.id === `subnet-${subnetInfo.vnet}-${subnetInfo.subnet}`
        )
        if (nsgNode && subnetNode) {
          console.log(`[EDGE DEBUG] Creating NSG->Subnet edge: ${nsgNode.id} -> ${subnetNode.id}`)
          generatedEdges.push({
            id: `edge-nsg-subnet-${nsgNode.id}-${subnetNode.id}`,
            source: nsgNode.id,
            target: subnetNode.id,
            type: 'smoothstep',
            animated: true,
            markerEnd: { type: MarkerType.ArrowClosed },
            style: { stroke: '#ef4444', strokeWidth: 2, strokeDasharray: '5,5' },
            label: 'protects',
          })
        }
      })

      console.log(`[EDGE DEBUG] Total edges created: ${generatedEdges.length}`)
      console.log('[EDGE DEBUG] All edges:', JSON.stringify(generatedEdges.slice(0, 3), null, 2))

      // TEST: Add a hardcoded edge between first two nodes to verify edges render
      if (generatedNodes.length >= 2) {
        const testEdge: Edge = {
          id: 'test-edge-001',
          source: generatedNodes[0].id,
          target: generatedNodes[1].id,
          type: 'default',
          style: { stroke: 'red', strokeWidth: 4 },
          animated: true,
        }
        generatedEdges.push(testEdge)
        console.log(`[EDGE DEBUG] Added test edge: ${generatedNodes[0].id} -> ${generatedNodes[1].id}`)
      }

      setNodes(generatedNodes)
      setEdges(generatedEdges)
      setShowLegend(true)
    } catch (err) {
      console.error('Failed to generate diagram:', err)
      setError('Failed to generate diagram from cloud data')
    } finally {
      setGenerating(false)
    }
  }

  // Helper to extract resource details for display
  // Azure API uses PascalCase property names
  const getResourceDetails = (resourceType: string, resource: any): string[] => {
    const details: string[] = []

    switch (resourceType) {
      case 'virtual_machines':
        if (resource.VmSize) details.push(`Size: ${resource.VmSize}`)
        if (resource.OsType) details.push(`OS: ${resource.OsType}`)
        if (resource.OsName) details.push(`${resource.OsName}`)
        if (resource.Location) details.push(`Location: ${resource.Location}`)
        if (resource.ProvisioningState) details.push(`State: ${resource.ProvisioningState}`)
        break
      case 'network_security_groups':
        if (resource.InboundRules) details.push(`Inbound: ${resource.InboundRules.length} rules`)
        if (resource.OutboundRules) details.push(`Outbound: ${resource.OutboundRules.length} rules`)
        if (resource.Location) details.push(`Location: ${resource.Location}`)
        break
      case 'web_apps':
        if (resource.HttpsOnly) details.push('HTTPS Only')
        if (resource.State) details.push(`State: ${resource.State}`)
        if (resource.HostNames?.length) details.push(`Hosts: ${resource.HostNames.length}`)
        break
      case 'sql_databases':
        if (resource.Sku) details.push(`SKU: ${resource.Sku}`)
        if (resource.MaxSizeBytes) details.push(`Size: ${Math.round(resource.MaxSizeBytes / 1024 / 1024 / 1024)} GB`)
        if (resource.Status) details.push(`Status: ${resource.Status}`)
        break
      case 'storage_accounts':
        if (resource.Sku) details.push(`SKU: ${resource.Sku}`)
        if (resource.AccessTier) details.push(`Tier: ${resource.AccessTier}`)
        if (resource.Kind) details.push(`Kind: ${resource.Kind}`)
        break
      case 'key_vaults':
        if (resource.Sku) details.push(`SKU: ${resource.Sku}`)
        if (resource.SoftDeleteEnabled) details.push('Soft Delete')
        if (resource.Location) details.push(`Location: ${resource.Location}`)
        break
      case 'virtual_networks':
        if (resource.AddressSpace?.length) details.push(`CIDR: ${resource.AddressSpace.join(', ')}`)
        if (resource.Subnets?.length) details.push(`Subnets: ${resource.Subnets.length}`)
        if (resource.Location) details.push(`Location: ${resource.Location}`)
        break
      case 'network_interfaces':
        if (resource.PrimaryPrivateIP) details.push(`IP: ${resource.PrimaryPrivateIP}`)
        if (resource.VirtualNetwork) details.push(`VNET: ${resource.VirtualNetwork}`)
        if (resource.Subnet) details.push(`Subnet: ${resource.Subnet}`)
        if (resource.HasPublicIP) details.push('Has Public IP')
        break
      case 'public_ip_addresses':
        if (resource.IpAddress) details.push(`IP: ${resource.IpAddress}`)
        if (resource.AllocationMethod) details.push(`Allocation: ${resource.AllocationMethod}`)
        if (resource.SKU) details.push(`SKU: ${resource.SKU}`)
        break
      case 'virtual_network_gateways':
        if (resource.GatewayType) details.push(`Type: ${resource.GatewayType}`)
        if (resource.VPNType) details.push(`VPN: ${resource.VPNType}`)
        if (resource.SKU) details.push(`SKU: ${resource.SKU}`)
        if (resource.BGPEnabled) details.push('BGP Enabled')
        break
    }

    return details
  }

  // Add custom node (user-defined component)
  const [newNodeType, setNewNodeType] = useState('waf')
  const [newNodeLabel, setNewNodeLabel] = useState('')
  const [newNodeSubtitle, setNewNodeSubtitle] = useState('')

  const addCustomNode = () => {
    if (!newNodeLabel) return

    // Calculate viewport center position
    const centerX = window.innerWidth / 2
    const centerY = window.innerHeight / 2
    const flowPosition = screenToFlowPosition({ x: centerX, y: centerY })

    const newNode: Node = {
      id: `custom-${Date.now()}`,
      type: 'customNode',
      position: flowPosition,
      data: {
        label: newNodeLabel,
        subtitle: newNodeSubtitle,
        customType: newNodeType,
        color: getCustomNodeColor(newNodeType),
      },
    }

    setNodes((nds) => [...nds, newNode])
    setNewNodeLabel('')
    setNewNodeSubtitle('')
    setShowAddNodeModal(false)
  }

  const getCustomNodeColor = (type: string): string => {
    switch (type) {
      case 'waf':
        return 'bg-pink-100 border-pink-400 text-pink-700'
      case 'cdn':
        return 'bg-teal-100 border-teal-400 text-teal-700'
      case 'loadbalancer':
        return 'bg-amber-100 border-amber-400 text-amber-700'
      case 'firewall':
        return 'bg-rose-100 border-rose-400 text-rose-700'
      case 'dns':
        return 'bg-sky-100 border-sky-400 text-sky-700'
      default:
        return 'bg-cyan-100 border-cyan-400 text-cyan-700'
    }
  }

  // Export functions - captures ENTIRE diagram including edges
  const exportDiagram = async (format: 'png' | 'svg' | 'pdf') => {
    if (!reactFlowWrapper.current || nodes.length === 0) return

    // Get the renderer element which contains BOTH viewport (nodes) AND edges SVG
    const flowElement = reactFlowWrapper.current.querySelector('.react-flow') as HTMLElement
    if (!flowElement) return

    try {
      // Calculate bounds of all nodes
      const nodesBounds = getNodesBounds(nodes)
      const padding = 50
      const imageWidth = Math.max(nodesBounds.width + padding * 2, 800)
      const imageHeight = Math.max(nodesBounds.height + padding * 2, 600)

      // Calculate viewport transform to fit all nodes
      const viewport = getViewportForBounds(
        nodesBounds,
        imageWidth,
        imageHeight,
        0.5, // minZoom
        2,   // maxZoom
        padding
      )

      // Hide controls and minimap during export
      const controls = flowElement.querySelector('.react-flow__controls') as HTMLElement
      const minimap = flowElement.querySelector('.react-flow__minimap') as HTMLElement
      const panel = flowElement.querySelector('.react-flow__panel') as HTMLElement
      if (controls) controls.style.display = 'none'
      if (minimap) minimap.style.display = 'none'
      if (panel) panel.style.display = 'none'

      // Get viewport element and apply transform for export
      const viewportEl = flowElement.querySelector('.react-flow__viewport') as HTMLElement
      const originalTransform = viewportEl?.style.transform || ''

      if (viewportEl) {
        viewportEl.style.transform = `translate(${viewport.x}px, ${viewport.y}px) scale(${viewport.zoom})`
      }

      // Force edge styles to be visible for export (html-to-image doesn't capture computed styles well)
      const edgePaths = flowElement.querySelectorAll('.react-flow__edge path')
      const originalEdgeStyles: string[] = []
      edgePaths.forEach((path, index) => {
        const pathEl = path as SVGPathElement
        originalEdgeStyles[index] = pathEl.getAttribute('style') || ''
        // Get computed stroke color or use default
        const computedStyle = window.getComputedStyle(pathEl)
        const stroke = computedStyle.stroke || '#b1b1b7'
        const strokeWidth = computedStyle.strokeWidth || '2'
        pathEl.setAttribute('style', `${originalEdgeStyles[index]}; stroke: ${stroke}; stroke-width: ${strokeWidth}; fill: none;`)
      })

      // Force marker (arrow) visibility
      const markers = flowElement.querySelectorAll('marker path')
      markers.forEach((marker) => {
        const markerEl = marker as SVGPathElement
        markerEl.setAttribute('fill', '#b1b1b7')
      })

      if (format === 'png') {
        const dataUrl = await toPng(flowElement, {
          backgroundColor: '#ffffff',
          quality: 1,
          width: imageWidth,
          height: imageHeight,
          style: {
            width: `${imageWidth}px`,
            height: `${imageHeight}px`,
          },
          filter: (node) => {
            // Filter out controls, minimap, attribution
            const className = node.className?.toString() || ''
            if (className.includes('react-flow__controls')) return false
            if (className.includes('react-flow__minimap')) return false
            if (className.includes('react-flow__attribution')) return false
            if (className.includes('react-flow__panel')) return false
            return true
          },
        })
        const link = document.createElement('a')
        link.download = `${selectedDiagram?.name || 'network-diagram'}.png`
        link.href = dataUrl
        link.click()
      } else if (format === 'svg') {
        const dataUrl = await toSvg(flowElement, {
          backgroundColor: '#ffffff',
          width: imageWidth,
          height: imageHeight,
          style: {
            width: `${imageWidth}px`,
            height: `${imageHeight}px`,
          },
          filter: (node) => {
            const className = node.className?.toString() || ''
            if (className.includes('react-flow__controls')) return false
            if (className.includes('react-flow__minimap')) return false
            if (className.includes('react-flow__attribution')) return false
            if (className.includes('react-flow__panel')) return false
            return true
          },
        })
        const link = document.createElement('a')
        link.download = `${selectedDiagram?.name || 'network-diagram'}.svg`
        link.href = dataUrl
        link.click()
      } else if (format === 'pdf') {
        const dataUrl = await toPng(flowElement, {
          backgroundColor: '#ffffff',
          quality: 1,
          width: imageWidth,
          height: imageHeight,
          style: {
            width: `${imageWidth}px`,
            height: `${imageHeight}px`,
          },
          filter: (node) => {
            const className = node.className?.toString() || ''
            if (className.includes('react-flow__controls')) return false
            if (className.includes('react-flow__minimap')) return false
            if (className.includes('react-flow__attribution')) return false
            if (className.includes('react-flow__panel')) return false
            return true
          },
        })

        // Create PDF with proper dimensions (landscape, scaled to fit)
        const pdfWidth = 297 // A4 landscape width in mm
        const pdfHeight = 210 // A4 landscape height in mm
        const aspectRatio = imageWidth / imageHeight
        const pdfAspectRatio = pdfWidth / pdfHeight

        let finalWidth = pdfWidth
        let finalHeight = pdfHeight
        let offsetX = 0
        let offsetY = 0

        if (aspectRatio > pdfAspectRatio) {
          // Image is wider - fit to width
          finalHeight = pdfWidth / aspectRatio
          offsetY = (pdfHeight - finalHeight) / 2
        } else {
          // Image is taller - fit to height
          finalWidth = pdfHeight * aspectRatio
          offsetX = (pdfWidth - finalWidth) / 2
        }

        const pdf = new jsPDF('landscape', 'mm', 'a4')
        pdf.addImage(dataUrl, 'PNG', offsetX, offsetY, finalWidth, finalHeight)
        pdf.save(`${selectedDiagram?.name || 'network-diagram'}.pdf`)
      }

      // Restore original viewport transform
      if (viewportEl) {
        viewportEl.style.transform = originalTransform
      }

      // Restore original edge styles
      edgePaths.forEach((path, index) => {
        const pathEl = path as SVGPathElement
        if (originalEdgeStyles[index]) {
          pathEl.setAttribute('style', originalEdgeStyles[index])
        } else {
          pathEl.removeAttribute('style')
        }
      })

      // Restore hidden elements
      if (controls) controls.style.display = ''
      if (minimap) minimap.style.display = ''
      if (panel) panel.style.display = ''
    } catch (err) {
      console.error('Failed to export diagram:', err)
      setError('Failed to export diagram')

      // Restore on error too
      const flowElement = reactFlowWrapper.current?.querySelector('.react-flow') as HTMLElement
      if (flowElement) {
        const controls = flowElement.querySelector('.react-flow__controls') as HTMLElement
        const minimap = flowElement.querySelector('.react-flow__minimap') as HTMLElement
        const panel = flowElement.querySelector('.react-flow__panel') as HTMLElement
        if (controls) controls.style.display = ''
        if (minimap) minimap.style.display = ''
        if (panel) panel.style.display = ''
      }
    }

    setShowExportMenu(false)
  }

  // Save diagram
  const saveDiagram = async () => {
    if (!diagramName && !selectedDiagram) {
      setError('Please enter a diagram name')
      return
    }

    setSaving(true)
    try {
      const layoutData = JSON.stringify({ nodes, edges })

      if (selectedDiagram) {
        // Update existing
        await api.updateDiagram(selectedDiagram.id, {
          name: diagramName || selectedDiagram.name,
          description: diagramDescription || selectedDiagram.description,
          layout_data: layoutData,
        })
      } else {
        // Create new
        await api.createDiagram({
          name: diagramName,
          description: diagramDescription,
          source_type: selectedIntegration ? 'azure' : 'manual',
          source_integration_id: selectedIntegration || undefined,
          layout_data: layoutData,
        })
      }

      await loadDiagrams()
      setShowCreateModal(false)
    } catch (err: unknown) {
      console.error('Failed to save diagram:', err)
      const errorMsg = err instanceof Error ? err.message : 'Failed to save diagram'
      setError(errorMsg)
    } finally {
      setSaving(false)
    }
  }

  // Load diagram
  const loadDiagram = async (diagram: Diagram) => {
    setSelectedDiagram(diagram)
    setDiagramName(diagram.name)
    setDiagramDescription(diagram.description || '')

    try {
      const response = await api.getDiagram(diagram.id)
      if (response.success && response.diagram) {
        const layoutData = response.diagram.layout_data
          ? JSON.parse(response.diagram.layout_data)
          : { nodes: [], edges: [] }

        // Migrate nodes: ensure resizable nodes have width/height as node properties
        const migratedNodes = (layoutData.nodes || []).map((node: Node) => {
          const data = node.data as Record<string, any>
          // For container, vnet, subnet, resourceGroup nodes - ensure width/height are node props
          if (['container', 'vnet', 'subnet', 'resourceGroup'].includes(node.type || '')) {
            return {
              ...node,
              width: node.width || data?.width || 400,
              height: node.height || data?.height || 300,
            }
          }
          return node
        })

        setNodes(migratedNodes)
        setEdges(layoutData.edges || [])
      }
    } catch (err) {
      console.error('Failed to load diagram:', err)
      setError('Failed to load diagram')
    }
  }

  // Delete diagram
  const deleteDiagram = async (id: string) => {
    if (!confirm('Are you sure you want to delete this diagram?')) return

    try {
      await api.deleteDiagram(id)
      if (selectedDiagram?.id === id) {
        setSelectedDiagram(null)
        setNodes([])
        setEdges([])
      }
      await loadDiagrams()
    } catch (err) {
      console.error('Failed to delete diagram:', err)
      setError('Failed to delete diagram')
    }
  }

  // New diagram
  const newDiagram = () => {
    setSelectedDiagram(null)
    setNodes([])
    setEdges([])
    setDiagramName('')
    setDiagramDescription('')
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Network Diagrams <span className="text-xs text-gray-400">v2</span></h1>
          <p className="text-gray-500 mt-1">
            Auto-generate infrastructure diagrams from cloud integrations
          </p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={newDiagram}
            className="inline-flex items-center gap-2 px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            <Plus className="h-4 w-4" />
            New Diagram
          </button>
          {nodes.length > 0 && (
            <>
              <div className="relative">
                <button
                  onClick={() => setShowExportMenu(!showExportMenu)}
                  className="inline-flex items-center gap-2 px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  <Download className="h-4 w-4" />
                  Export
                  <ChevronDown className="h-4 w-4" />
                </button>
                {showExportMenu && (
                  <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-10">
                    <button
                      onClick={() => exportDiagram('png')}
                      className="flex items-center gap-2 w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                    >
                      <FileImage className="h-4 w-4" />
                      Export as PNG
                    </button>
                    <button
                      onClick={() => exportDiagram('svg')}
                      className="flex items-center gap-2 w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                    >
                      <FileImage className="h-4 w-4" />
                      Export as SVG
                    </button>
                    <button
                      onClick={() => exportDiagram('pdf')}
                      className="flex items-center gap-2 w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                    >
                      <FileText className="h-4 w-4" />
                      Export as PDF
                    </button>
                  </div>
                )}
              </div>
              <button
                onClick={() => selectedDiagram ? saveDiagram() : setShowCreateModal(true)}
                disabled={saving}
                className="inline-flex items-center gap-2 px-4 py-2 text-white bg-primary-600 rounded-lg hover:bg-primary-700 disabled:opacity-50"
              >
                <Save className="h-4 w-4" />
                {saving ? 'Saving...' : 'Save'}
              </button>
            </>
          )}
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-3">
          <AlertCircle className="h-5 w-5 text-red-500 flex-shrink-0" />
          <p className="text-red-700">{error}</p>
          <button onClick={() => setError(null)} className="ml-auto text-red-500 hover:text-red-700">
            <X className="h-5 w-5" />
          </button>
        </div>
      )}

      <div className="grid grid-cols-12 gap-6">
        {/* Sidebar - Saved Diagrams */}
        <div className="col-span-3 bg-white rounded-lg border border-gray-200 p-4">
          <h3 className="font-semibold text-gray-900 mb-4">Saved Diagrams</h3>

          {loading ? (
            <div className="flex items-center justify-center py-8">
              <RefreshCw className="h-6 w-6 text-gray-400 animate-spin" />
            </div>
          ) : diagrams.length === 0 ? (
            <p className="text-sm text-gray-500 py-4">No diagrams yet. Generate one from your cloud integrations.</p>
          ) : (
            <div className="space-y-2">
              {diagrams.map((diagram) => (
                <div
                  key={diagram.id}
                  className={`p-3 rounded-lg border cursor-pointer transition-colors ${
                    selectedDiagram?.id === diagram.id
                      ? 'border-primary-500 bg-primary-50'
                      : 'border-gray-200 hover:bg-gray-50'
                  }`}
                  onClick={() => loadDiagram(diagram)}
                >
                  <div className="flex items-center justify-between">
                    <span className="font-medium text-sm">{diagram.name}</span>
                    <button
                      onClick={(e) => {
                        e.stopPropagation()
                        deleteDiagram(diagram.id)
                      }}
                      className="text-gray-400 hover:text-red-500"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                  {diagram.description && (
                    <p className="text-xs text-gray-500 mt-1 truncate">{diagram.description}</p>
                  )}
                  <div className="flex items-center gap-2 mt-2">
                    <span className={`text-xs px-2 py-0.5 rounded-full ${
                      diagram.source_type === 'azure' ? 'bg-blue-100 text-blue-700' :
                      diagram.source_type === 'aws' ? 'bg-orange-100 text-orange-700' :
                      diagram.source_type === 'gcp' ? 'bg-green-100 text-green-700' :
                      'bg-gray-100 text-gray-700'
                    }`}>
                      {diagram.source_type}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Generate from Cloud Section */}
          <div className="mt-6 pt-6 border-t border-gray-200">
            <h4 className="font-medium text-gray-900 mb-3">Generate from Cloud</h4>

            {cloudIntegrations.length === 0 ? (
              <p className="text-sm text-gray-500">
                No active cloud integrations found. Add an Azure, AWS, or GCP integration first.
              </p>
            ) : (
              <>
                <select
                  value={selectedIntegration}
                  onChange={(e) => setSelectedIntegration(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                >
                  {cloudIntegrations.map((integration) => (
                    <option key={integration.id} value={integration.id}>
                      {integration.name} ({integration.type})
                    </option>
                  ))}
                </select>
                <button
                  onClick={generateFromCloud}
                  disabled={generating || !selectedIntegration}
                  className="mt-3 w-full inline-flex items-center justify-center gap-2 px-4 py-2 text-white bg-primary-600 rounded-lg hover:bg-primary-700 disabled:opacity-50"
                >
                  {generating ? (
                    <>
                      <RefreshCw className="h-4 w-4 animate-spin" />
                      Generating...
                    </>
                  ) : (
                    <>
                      <Sparkles className="h-4 w-4" />
                      Generate Diagram
                    </>
                  )}
                </button>
              </>
            )}
          </div>
        </div>

        {/* Main Diagram Canvas */}
        <div className="col-span-9">
          <div
            ref={reactFlowWrapper}
            className="bg-white rounded-lg border border-gray-200"
            style={{ height: '70vh' }}
          >
            {nodes.length === 0 ? (
              <div className="h-full flex flex-col items-center justify-center text-gray-500">
                <Network className="h-16 w-16 mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">No Diagram</h3>
                <p className="text-sm mb-4">
                  Generate a diagram from your cloud integrations or create one manually.
                </p>
                {cloudIntegrations.length > 0 && (
                  <button
                    onClick={generateFromCloud}
                    disabled={generating}
                    className="inline-flex items-center gap-2 px-4 py-2 text-white bg-primary-600 rounded-lg hover:bg-primary-700 disabled:opacity-50"
                  >
                    <Sparkles className="h-4 w-4" />
                    Generate from Cloud
                  </button>
                )}
              </div>
            ) : (
              <>
              {console.log('[RENDER] edges count:', filteredEdges.length, 'nodes count:', filteredNodes.length, 'hidden types:', hiddenTypes.size)}
              {filteredEdges.length > 0 && console.log('[RENDER] First edge:', filteredEdges[0])}
              <ReactFlow
                nodes={filteredNodes}
                edges={filteredEdges}
                defaultEdgeOptions={{ type: 'smoothstep', animated: true }}
                onNodesChange={onNodesChange}
                onEdgesChange={onEdgesChange}
                onConnect={onConnect}
                onNodeDoubleClick={onNodeDoubleClick}
                nodeTypes={nodeTypes}
                fitView
                attributionPosition="bottom-left"
                deleteKeyCode={null}
                selectionKeyCode={selectionMode ? null : "Shift"}
                multiSelectionKeyCode="Shift"
                edgesFocusable={true}
                elementsSelectable={true}
                selectionOnDrag={selectionMode}
                panOnDrag={selectionMode ? [1, 2] : true}
                selectionMode={SelectionMode.Partial}
              >
                <Controls />
                <MiniMap />
                <Background variant={BackgroundVariant.Dots} gap={12} size={1} />

                {/* Toolbar Panel */}
                <Panel position="top-right" className="bg-white rounded-lg shadow border border-gray-200 p-2 flex flex-col gap-1">
                  <button
                    onClick={() => setSelectionMode(!selectionMode)}
                    className={`inline-flex items-center gap-2 px-3 py-1.5 text-sm rounded ${
                      selectionMode
                        ? 'bg-primary-100 text-primary-700 border border-primary-300'
                        : 'text-gray-700 hover:bg-gray-100'
                    }`}
                    title={selectionMode ? 'Click to disable box selection (use middle/right mouse to pan)' : 'Click to enable box selection'}
                  >
                    <MousePointer2 className="h-4 w-4" />
                    {selectionMode ? 'Selection Mode' : 'Select Multiple'}
                  </button>
                  <div className="border-t border-gray-200 my-1" />
                  <button
                    onClick={() => setShowAddNodeModal(true)}
                    className="inline-flex items-center gap-2 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-100 rounded"
                  >
                    <Plus className="h-4 w-4" />
                    Add Component
                  </button>
                  <button
                    onClick={() => setShowAddContainerModal(true)}
                    className="inline-flex items-center gap-2 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-100 rounded"
                  >
                    <Square className="h-4 w-4" />
                    Add Container
                  </button>
                  <button
                    onClick={() => setShowAddTextModal(true)}
                    className="inline-flex items-center gap-2 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-100 rounded"
                  >
                    <Type className="h-4 w-4" />
                    Add Text
                  </button>
                  {nodes.some((n) => n.selected) && (
                    <button
                      onClick={deleteSelectedNodes}
                      className="inline-flex items-center gap-2 px-3 py-1.5 text-sm text-red-600 hover:bg-red-50 rounded"
                    >
                      <Trash2 className="h-4 w-4" />
                      Delete Selected
                    </button>
                  )}
                </Panel>

                {/* Legend/Filter Panel */}
                {showLegend && activeLegendItems.length > 0 && (
                  <Panel position="bottom-left" className="bg-white rounded-lg shadow border border-gray-200 m-4">
                    <div className="flex items-center justify-between px-3 py-2 border-b border-gray-200">
                      <span className="text-sm font-medium text-gray-700">
                        Filter
                        {(hiddenTypes.size > 0 || selectedResourceGroups.size > 0) && (
                          <span className="ml-1 text-gray-500 font-normal">
                            ({hiddenTypes.size > 0 && `${hiddenTypes.size} types hidden`}
                            {hiddenTypes.size > 0 && selectedResourceGroups.size > 0 && ', '}
                            {selectedResourceGroups.size > 0 && `${selectedResourceGroups.size} RG selected`})
                          </span>
                        )}
                      </span>
                      <div className="flex items-center gap-1">
                        {(hiddenTypes.size > 0 || selectedResourceGroups.size > 0) && (
                          <button
                            onClick={clearAllFilters}
                            className="px-2 py-0.5 text-xs text-primary-600 hover:bg-primary-50 rounded"
                          >
                            Clear All
                          </button>
                        )}
                        <button
                          onClick={() => setLegendCollapsed(!legendCollapsed)}
                          className="p-1 hover:bg-gray-100 rounded"
                        >
                          {legendCollapsed ? (
                            <ChevronUp className="h-4 w-4 text-gray-500" />
                          ) : (
                            <ChevronDown className="h-4 w-4 text-gray-500" />
                          )}
                        </button>
                        <button
                          onClick={() => setShowLegend(false)}
                          className="p-1 hover:bg-gray-100 rounded"
                        >
                          <X className="h-4 w-4 text-gray-500" />
                        </button>
                      </div>
                    </div>
                    {!legendCollapsed && (
                      <div className="max-h-[400px] overflow-y-auto">
                        {/* Resource Groups Section */}
                        {availableResourceGroups.length > 1 && (
                          <div className="p-3 border-b border-gray-100">
                            <p className="text-xs font-medium text-gray-600 mb-2">Resource Groups</p>
                            <p className="text-xs text-gray-400 mb-2">
                              {selectedResourceGroups.size === 0 ? 'Showing all' : 'Click to toggle'}
                            </p>
                            <div className="space-y-1">
                              {availableResourceGroups.map((rg) => {
                                const isSelected = selectedResourceGroups.has(rg)
                                const isFiltering = selectedResourceGroups.size > 0
                                return (
                                  <button
                                    key={rg}
                                    onClick={() => toggleResourceGroup(rg)}
                                    className={`flex items-center gap-2 w-full px-2 py-1.5 rounded hover:bg-gray-100 transition-all text-left ${
                                      isFiltering && !isSelected ? 'opacity-40' : ''
                                    }`}
                                  >
                                    <div className={`w-3 h-3 rounded border ${
                                      isSelected ? 'bg-primary-600 border-primary-600' : 'border-gray-400'
                                    }`}>
                                      {isSelected && (
                                        <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 12 12">
                                          <path d="M10.28 2.28L3.989 8.575 1.695 6.28A1 1 0 00.28 7.695l3 3a1 1 0 001.414 0l7-7A1 1 0 0010.28 2.28z" />
                                        </svg>
                                      )}
                                    </div>
                                    <span className={`text-sm text-gray-700 truncate ${
                                      isFiltering && !isSelected ? 'line-through' : ''
                                    }`}>
                                      {rg}
                                    </span>
                                  </button>
                                )
                              })}
                            </div>
                          </div>
                        )}

                        {/* Resource Types Section */}
                        <div className="p-3">
                          <p className="text-xs font-medium text-gray-600 mb-2">Resource Types</p>
                          <p className="text-xs text-gray-400 mb-2">Click to hide/show</p>
                          <div className="space-y-1">
                            {activeLegendItems.map((item) => {
                              const isHidden = hiddenTypes.has(item.type)
                              return (
                                <button
                                  key={item.type}
                                  onClick={() => toggleTypeVisibility(item.type)}
                                  className={`flex items-center gap-2 w-full px-2 py-1.5 rounded hover:bg-gray-100 transition-all ${
                                    isHidden ? 'opacity-40' : ''
                                  }`}
                                >
                                  <div className={`p-1.5 rounded border ${item.color} ${isHidden ? 'grayscale' : ''}`}>
                                    <item.icon className="h-4 w-4" />
                                  </div>
                                  <span className={`text-sm text-gray-700 ${isHidden ? 'line-through' : ''}`}>
                                    {item.label}
                                  </span>
                                  {isHidden && (
                                    <span className="ml-auto text-xs text-gray-400">hidden</span>
                                  )}
                                </button>
                              )
                            })}
                          </div>
                        </div>
                      </div>
                    )}
                  </Panel>
                )}

                {/* Show Legend Button (when hidden) */}
                {!showLegend && (
                  <Panel position="bottom-left" className="m-4">
                    <button
                      onClick={() => setShowLegend(true)}
                      className="bg-white rounded-lg shadow border border-gray-200 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
                    >
                      Show Legend
                    </button>
                  </Panel>
                )}
              </ReactFlow>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Save Diagram Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">Save Diagram</h3>
              <button onClick={() => setShowCreateModal(false)} className="text-gray-400 hover:text-gray-600">
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                <input
                  type="text"
                  value={diagramName}
                  onChange={(e) => setDiagramName(e.target.value)}
                  placeholder="e.g., Production Infrastructure"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea
                  value={diagramDescription}
                  onChange={(e) => setDiagramDescription(e.target.value)}
                  placeholder="Optional description..."
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                />
              </div>
            </div>
            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => setShowCreateModal(false)}
                className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg"
              >
                Cancel
              </button>
              <button
                onClick={saveDiagram}
                disabled={!diagramName || saving}
                className="px-4 py-2 text-white bg-primary-600 rounded-lg hover:bg-primary-700 disabled:opacity-50"
              >
                {saving ? 'Saving...' : 'Save Diagram'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Custom Node Modal */}
      {showAddNodeModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">Add Component</h3>
              <button onClick={() => setShowAddNodeModal(false)} className="text-gray-400 hover:text-gray-600">
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Component Type</label>
                <select
                  value={newNodeType}
                  onChange={(e) => setNewNodeType(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                >
                  <option value="waf">WAF (Web Application Firewall)</option>
                  <option value="cdn">CDN (Content Delivery Network)</option>
                  <option value="loadbalancer">Load Balancer</option>
                  <option value="firewall">Firewall</option>
                  <option value="dns">DNS Service</option>
                  <option value="other">Other</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                <input
                  type="text"
                  value={newNodeLabel}
                  onChange={(e) => setNewNodeLabel(e.target.value)}
                  placeholder="e.g., Cloudflare WAF"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description (optional)</label>
                <input
                  type="text"
                  value={newNodeSubtitle}
                  onChange={(e) => setNewNodeSubtitle(e.target.value)}
                  placeholder="e.g., Edge protection"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                />
              </div>
            </div>
            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => setShowAddNodeModal(false)}
                className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg"
              >
                Cancel
              </button>
              <button
                onClick={addCustomNode}
                disabled={!newNodeLabel}
                className="px-4 py-2 text-white bg-primary-600 rounded-lg hover:bg-primary-700 disabled:opacity-50"
              >
                Add Component
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Container Modal */}
      {showAddContainerModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">Add Container</h3>
              <button onClick={() => setShowAddContainerModal(false)} className="text-gray-400 hover:text-gray-600">
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Label</label>
                <input
                  type="text"
                  value={containerLabel}
                  onChange={(e) => setContainerLabel(e.target.value)}
                  placeholder="e.g., Production Environment"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Border Style</label>
                <select
                  value={containerBorderStyle}
                  onChange={(e) => setContainerBorderStyle(e.target.value as 'solid' | 'dashed')}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                >
                  <option value="solid">Solid</option>
                  <option value="dashed">Dashed</option>
                </select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Border Color</label>
                  <input
                    type="color"
                    value={containerBorderColor}
                    onChange={(e) => setContainerBorderColor(e.target.value)}
                    className="w-full h-10 border border-gray-300 rounded-lg cursor-pointer"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Background</label>
                  <select
                    value={containerBgColor}
                    onChange={(e) => setContainerBgColor(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  >
                    <option value="rgba(243, 244, 246, 0.5)">Gray</option>
                    <option value="rgba(219, 234, 254, 0.5)">Blue</option>
                    <option value="rgba(220, 252, 231, 0.5)">Green</option>
                    <option value="rgba(254, 243, 199, 0.5)">Yellow</option>
                    <option value="rgba(254, 226, 226, 0.5)">Red</option>
                    <option value="rgba(237, 233, 254, 0.5)">Purple</option>
                    <option value="transparent">Transparent</option>
                  </select>
                </div>
              </div>
            </div>
            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => setShowAddContainerModal(false)}
                className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg"
              >
                Cancel
              </button>
              <button
                onClick={addContainer}
                className="px-4 py-2 text-white bg-primary-600 rounded-lg hover:bg-primary-700"
              >
                Add Container
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Text Annotation Modal */}
      {showAddTextModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">Add Text Annotation</h3>
              <button onClick={() => setShowAddTextModal(false)} className="text-gray-400 hover:text-gray-600">
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Text</label>
                <textarea
                  value={textContent}
                  onChange={(e) => setTextContent(e.target.value)}
                  placeholder="Enter your text..."
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Font Size</label>
                  <select
                    value={textFontSize}
                    onChange={(e) => setTextFontSize(e.target.value as 'small' | 'medium' | 'large')}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  >
                    <option value="small">Small</option>
                    <option value="medium">Medium</option>
                    <option value="large">Large</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Color</label>
                  <input
                    type="color"
                    value={textColor}
                    onChange={(e) => setTextColor(e.target.value)}
                    className="w-full h-10 border border-gray-300 rounded-lg cursor-pointer"
                  />
                </div>
              </div>
              <div className="flex items-center gap-4">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={textBold}
                    onChange={(e) => setTextBold(e.target.checked)}
                    className="h-4 w-4 rounded border-gray-300 text-primary-600"
                  />
                  <span className="text-sm font-medium text-gray-700">Bold</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={textItalic}
                    onChange={(e) => setTextItalic(e.target.checked)}
                    className="h-4 w-4 rounded border-gray-300 text-primary-600"
                  />
                  <span className="text-sm font-medium text-gray-700">Italic</span>
                </label>
              </div>
            </div>
            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => setShowAddTextModal(false)}
                className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg"
              >
                Cancel
              </button>
              <button
                onClick={addTextAnnotation}
                disabled={!textContent}
                className="px-4 py-2 text-white bg-primary-600 rounded-lg hover:bg-primary-700 disabled:opacity-50"
              >
                Add Text
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Node Modal */}
      {showEditModal && editingNode && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">Edit Node</h3>
              <button onClick={() => setShowEditModal(false)} className="text-gray-400 hover:text-gray-600">
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Label</label>
                <input
                  type="text"
                  value={editLabel}
                  onChange={(e) => setEditLabel(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Subtitle</label>
                <input
                  type="text"
                  value={editSubtitle}
                  onChange={(e) => setEditSubtitle(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Details (one per line)</label>
                <textarea
                  value={editDetails.join('\n')}
                  onChange={(e) => setEditDetails(e.target.value.split('\n'))}
                  rows={4}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  placeholder="Enter details, one per line..."
                />
              </div>
            </div>
            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => setShowEditModal(false)}
                className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg"
              >
                Cancel
              </button>
              <button
                onClick={saveEditedNode}
                className="px-4 py-2 text-white bg-primary-600 rounded-lg hover:bg-primary-700"
              >
                Save Changes
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

// Wrap with ReactFlowProvider
export default function Diagrams() {
  return (
    <ReactFlowProvider>
      <DiagramsContent />
    </ReactFlowProvider>
  )
}
